import { useState } from "react";
import { X, Share2, Mail, Link2, Copy, Loader2, CheckCircle2, Clock } from "lucide-react";

const EXPIRY_OPTIONS = [
  { value: null, label: "Never expires" },
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

export default function ShareModal({ open, onClose, certId, onShare }) {
  const [email, setEmail] = useState("");
  const [expiryDays, setExpiryDays] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleShare = async (withEmail) => {
    setLoading(true);
    try {
      const data = await onShare(certId, withEmail ? email : "", expiryDays);
      setResult(data);
    } catch { /* handled by parent */ }
    finally { setLoading(false); }
  };

  const copyUrl = () => {
    if (result?.url) navigator.clipboard.writeText(result.url).catch(() => {});
  };

  const handleClose = () => { setEmail(""); setResult(null); setExpiryDays(null); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="share-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-md mx-4 rounded-2xl p-6 animate-fade-in-up"
        style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

        <button onClick={handleClose} className="absolute top-4 right-4 hover:opacity-70 transition-opacity" data-testid="share-modal-close">
          <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
        </button>

        {result ? (
          <div className="text-center" data-testid="share-success">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#EAFBF0' }}>
              <CheckCircle2 className="w-7 h-7 text-[#22C55E]" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
              {result.emailed ? "Link Emailed!" : "Link Created!"}
            </h3>
            <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
              {result.emailed ? `Share link sent to ${email}` : "Copy the link below to share with your customer"}
            </p>
            <div className="flex items-center gap-2 p-3 rounded-xl mb-4" style={{ backgroundColor: 'var(--row-alt)' }}>
              <Link2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
              <input readOnly value={result.url} className="flex-1 text-xs bg-transparent outline-none truncate" style={{ color: 'var(--text-primary)', fontFamily: 'IBM Plex Mono, monospace' }} />
              <button onClick={copyUrl} className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/50 transition-colors" data-testid="copy-share-link">
                <Copy className="w-3.5 h-3.5 text-[#00A3E0]" />
              </button>
            </div>
            <button onClick={handleClose} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors">Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F7FC' }}>
                <Share2 className="w-5 h-5 text-[#00A3E0]" />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Share Certificate</h3>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Send a view-only link to your customer</p>
              </div>
            </div>

            {/* Email Input */}
            <div className="mb-4">
              <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>
                <Mail className="w-3.5 h-3.5 inline mr-1" /> Customer Email (optional)
              </label>
              <input data-testid="share-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="brutalist-input" placeholder="customer@email.co.uk" />
            </div>

            {/* Expiry Selector */}
            <div className="mb-5">
              <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>
                <Clock className="w-3.5 h-3.5 inline mr-1" /> Link expires in
              </label>
              <div className="flex gap-2" data-testid="expiry-selector">
                {EXPIRY_OPTIONS.map((opt) => (
                  <button key={String(opt.value)} data-testid={`expiry-${opt.value || 'never'}`}
                    onClick={() => setExpiryDays(opt.value)}
                    className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all border"
                    style={{
                      backgroundColor: expiryDays === opt.value ? '#00A3E0' : 'var(--bg-input)',
                      color: expiryDays === opt.value ? 'white' : 'var(--text-secondary)',
                      borderColor: expiryDays === opt.value ? '#00A3E0' : 'var(--border-main)',
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {email && (
                <button data-testid="share-email-btn" onClick={() => handleShare(true)} disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#D4A537] hover:bg-[#B8892C] transition-colors">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email Link
                </button>
              )}
              <button data-testid="share-link-btn" onClick={() => handleShare(false)} disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Get Link
              </button>
            </div>

            <p className="text-[10px] mt-3 text-center" style={{ color: 'var(--text-muted)' }}>
              Customers can view and download the certificate — no login required
            </p>
          </>
        )}
      </div>
    </div>
  );
}
